import React from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
  Box,
} from '@mui/material';
import { Sync as SyncIcon, ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, Save as SaveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';

interface ButtonProps extends Omit<MuiButtonProps, 'color'> {
  loading?: boolean;
  loadingText?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  loading = false,
  loadingText,
  disabled,
  startIcon,
  endIcon,
  sx,
  ...props
}) => {
  return (
    <MuiButton
      {...props}
      disabled={disabled || loading}
      startIcon={loading ? undefined : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 44, // Touch target minimo per mobile
        ...sx,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} color="inherit" />
          {loadingText && (
            <span>{loadingText}</span>
          )}
        </Box>
      ) : (
        children
      )}
    </MuiButton>
  );
};

// Pulsanti specializzati per casi d'uso comuni
export const PrimaryButton: React.FC<Omit<ButtonProps, 'color' | 'variant'>> = (props) => (
  <Button {...props} color="primary" variant="contained" />
);

export const SecondaryButton: React.FC<Omit<ButtonProps, 'color' | 'variant'>> = (props) => (
  <Button {...props} color="secondary" variant="outlined" />
);

export const SuccessButton: React.FC<Omit<ButtonProps, 'color' | 'variant'>> = (props) => (
  <Button {...props} color="success" variant="contained" />
);

export const WarningButton: React.FC<Omit<ButtonProps, 'color' | 'variant'>> = (props) => (
  <Button {...props} color="warning" variant="contained" />
);

export const ErrorButton: React.FC<Omit<ButtonProps, 'color' | 'variant'>> = (props) => (
  <Button {...props} color="error" variant="contained" />
);

export const InfoButton: React.FC<Omit<ButtonProps, 'color' | 'variant'>> = (props) => (
  <Button {...props} color="info" variant="contained" />
);

// Pulsanti per azioni specifiche
export const SyncButton: React.FC<{
  onClick: () => void;
  loading?: boolean;
  pendingCount?: number;
  disabled?: boolean;
}> = ({ onClick, loading = false, pendingCount = 0, disabled = false }) => (
  <Button
    variant="outlined"
    color="primary"
    onClick={onClick}
    loading={loading}
    loadingText="Sincronizzando..."
    disabled={disabled || pendingCount === 0}
    startIcon={loading ? undefined : <SyncIcon />}
    sx={{
      minWidth: 140,
      position: 'relative',
    }}
  >
    {!loading && (
      <>
        Sincronizza
        {pendingCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              minWidth: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}
          >
            {pendingCount}
          </Box>
        )}
      </>
    )}
  </Button>
);

export const BackButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
}> = ({ onClick, disabled = false }) => (
  <Button
    variant="outlined"
    color="secondary"
    onClick={onClick}
    disabled={disabled}
    startIcon={<ArrowBackIcon />}
    sx={{ minWidth: 120 }}
  >
    Indietro
  </Button>
);

export const NextButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}> = ({ onClick, disabled = false, loading = false }) => (
  <Button
    variant="contained"
    color="primary"
    onClick={onClick}
    disabled={disabled}
    loading={loading}
    loadingText="Caricamento..."
    endIcon={loading ? undefined : <ArrowForwardIcon />}
    sx={{ minWidth: 120 }}
  >
    Avanti
  </Button>
);

export const SaveButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}> = ({ onClick, disabled = false, loading = false }) => (
  <Button
    variant="contained"
    color="success"
    onClick={onClick}
    disabled={disabled}
    loading={loading}
    loadingText="Salvando..."
    startIcon={loading ? undefined : <SaveIcon />}
    sx={{ minWidth: 120 }}
  >
    Salva
  </Button>
);

export const DeleteButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}> = ({ onClick, disabled = false, loading = false }) => (
  <Button
    variant="outlined"
    color="error"
    onClick={onClick}
    disabled={disabled}
    loading={loading}
    loadingText="Eliminando..."
    startIcon={loading ? undefined : <DeleteIcon />}
    sx={{ minWidth: 120 }}
  >
    Elimina
  </Button>
);
